import React from 'react';
import './About.css';

const founders = [
  {
    name: 'Christine Fernandez',
    title: 'Co-Founder and Head of Product',
    image: '/Christine1.jpg',
    credentials: ['Biomedical Engineering @ Johns Hopkins University'],
    dancer: 'Dancer for 19 years',
    bio: 'Christine created PointeSense’s initial concept which emerged from a biomedical engineering design course at Johns Hopkins, and is a lifelong ballet dancer. She served three years on the executive board of JHU’s Student Ballet Company, including a year as President, growing it from 10 members to a community of 40+ while dancing lead roles herself, including Juliet in the company’s Romeo and Juliet. Her close ties to the pointe shoe industry directly shape PointeSense’s product and market direction.',
  },
  {
    name: 'Kaitlyn Kumar',
    title: 'Co-Founder and Head of Technology',
    image: '/Kaitlyn1.JPG',
    credentials: [
      'M.S.E. Robotics @ Johns Hopkins University',
      'B.S. Mechanical Engineering and Dance Minor @ University of Southern California',
    ],
    dancer: 'Dancer for 20 years',
    bio: 'Kaitlyn works at the intersection of humans and machines: in research spanning dancing robots and autonomous surgery, on an award-winning senior design project building a lower-limb exoskeleton to assist human locomotion, and as Chief Engineer and ergonomics/driver interfaces lead for USC’s Formula SAE Racing team. That same instinct for how people and machines move together is what led her to PointeSense. She danced principal roles including Black Swan/Odile in Swan Lake, Mercedes in Don Quixote, and the Snow Queen in Nutcracker with JHU’s Student Ballet Company, debuted her own choreography in showcases, and trained across contemporary, ballroom, and hip hop in addition to ballet. That same fluency across two disparate worlds, dance and engineering, is what PointeSense is built on, and it earned her USC’s university-wide Renaissance Prize. She’s also worked professionally in aerospace and medical device design, and brings that same rigor to PointeSense’s technology.',
    press: [
      { label: 'USC Viterbi: A Graduating Engineer’s Secret to Success? Just Dance.', url: 'https://viterbischool.usc.edu/news/2024/05/a-graduating-engineers-secret-to-success-just-dance/' },
      { label: 'USC Viterbi Magazine: This Robot’s Got Rhythm', url: 'https://magazine.viterbi.usc.edu/spring-2023/engineering/this-robots-got-rhythm/' },
      { label: 'USC Viterbi Magazine: The Mechanics of Motion', url: 'https://magazine.viterbi.usc.edu/fall-2021/student-life/the-mechanics-of-motion-a-conversation-with-a-ballerina/' },
    ],
  },
];

const team = [
  {
    name: 'Isha Yeleswarapu',
    title: 'Motion ML Engineer',
    credentials: 'M.S.E. Biomedical Engineering @ Johns Hopkins University',
  },
  {
    name: 'Patrick Kumar',
    title: 'Sensor Fusion Engineer',
    credentials: 'Mechanical Engineering @ California Polytechnic University, San Luis Obispo',
  },
];

const advisors = [
  {
    name: 'Nitish Thakor, PhD',
    title: 'Professor of Biomedical Engineering, Johns Hopkins University',
  },
  {
    name: 'Kelly Schmutte',
    title: 'CEO, PerfectFit Pointe',
  },
  {
    name: 'Rhea Lwin',
    title: 'Owner, On Pointe Dancewear',
  },
];

const getInitials = (name) =>
  name
    .replace(/,.*$/, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const PersonBubble = ({ person }) => (
  <div className="person-card">
    <div className="person-bubble person-bubble-avatar">
      <span>{getInitials(person.name)}</span>
    </div>
    <h3 className="person-name">{person.name}</h3>
    <p className="person-title">{person.title}</p>
    {person.credentials && <p className="person-credentials">{person.credentials}</p>}
  </div>
);

const About = () => {
  return (
    <div className="about-container">
      {/* Story */}
      <section
        className="about-section about-story-section"
        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/snowF24.jpg)` }}
      >
        <div className="about-story-overlay"></div>
        <div className="about-content-wrapper about-story-wrapper about-story-content">
          <h1 className="about-heading">Our Story</h1>
          <p className="about-text">
            PointeSense came from two different perspectives in the world of ballet.
          </p>
          <p className="about-text">
            Christine grew up dancing ballet seriously, but was held back from pointe work by
            hereditary bunions until she was an adult. Approaching pointe with strong technique and
            intuition, but the nerves of an adult beginner, she found herself questioning whether she
            was correctly aligned, and wishing there was a tool that could show her what was actually
            happening the moment she stepped onto pointe.
          </p>
          <p className="about-text">
            Kaitlyn came at it from the angle of injury prevention. She started pointe work at age 9,
            and two years ago tore ligaments in her ankle during a performance. She remembers being
            told, as many dancers are, to push through the pain, and thinks a combination of poor
            pointe shoe fit and muscle overuse likely contributed to her injury. Seeing PointeSense&rsquo;s
            first prototype, she began wondering whether technology like it could surface the patterns
            in fit, technique, or imbalance that lead to injury before they do.
          </p>
          <p className="about-text">
            Together, they hope PointeSense can give dancers more insight into their own bodies and
            shoes, so they can train smarter, stay safer, and keep dancing longer.
          </p>
        </div>
      </section>

      {/* Founders */}
      <section className="about-section about-section-tint">
        <div className="about-content-wrapper about-founders-wrapper">
          <h2 className="about-heading">Meet the Founders</h2>
          <div className="founder-grid">
            {founders.map((founder) => (
              <div className="founder-card" key={founder.name}>
                <div className="founder-bubble">
                  <img src={founder.image} alt={founder.name} />
                </div>
                <h3 className="founder-name">{founder.name}</h3>
                <p className="founder-title">{founder.title}</p>
                {founder.credentials.map((line) => (
                  <p className="founder-credentials" key={line}>{line}</p>
                ))}
                <p className="founder-dancer">{founder.dancer}</p>
                <p className="founder-bio">{founder.bio}</p>
                {founder.press && (
                  <div className="founder-press">
                    <h4>Press</h4>
                    <ul>
                      {founder.press.map((item) => (
                        <li key={item.url}>
                          <a href={item.url} target="_blank" rel="noopener noreferrer">
                            {item.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="about-section">
        <div className="about-content-wrapper">
          <h2 className="about-heading">Team</h2>
          <div className="people-grid">
            {team.map((person) => (
              <PersonBubble person={person} key={person.name} />
            ))}
          </div>
        </div>
      </section>

      {/* Advisors */}
      <section className="about-section about-section-tint">
        <div className="about-content-wrapper">
          <h2 className="about-heading">Advisors</h2>
          <div className="people-grid">
            {advisors.map((person) => (
              <PersonBubble person={person} key={person.name} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
